/**
 * ISG Customer Certificates — Main Entry Point
 */
import '@isg/ui-kit/css/tokens.css';
import '@isg/ui-kit/css/reset.css';
import '@isg/ui-kit/css/components.css';
import '@isg/ui-kit/css/layout.css';

import { router, store, auth , checkEnvBanner } from '@isg/ui-kit';
import { renderNavigation, renderMobileHeader, createSidebarOverlay } from '@isg/ui-kit';
import { applyBranding, loadBranding } from '@isg/ui-kit';
import { createNotificationBell, createNotificationSidebar } from './components/notification-sidebar.js';
import { renderAIAssistant } from './components/ai-assistant-widget.js';
import { t, initI18n } from './i18n/index.js';

// The customer-facing upload form's language until customers get their own
// (their ERP entity's) — see init().
const CUSTOMER_FORM_LOCALE = 'en-GB'; // i18n-ignore: locale code, not display text

/* SSO: capture handoff from intranet broker */
auth.captureSSOHandoff();

// Built at render time, never at module scope: t() must run after bootI18n().
function navConfig() {
  return [
    {
      section: null,
      items: [
        { path: '/', label: t('nav.dashboard'), icon: 'layout-dashboard' },
      ],
    },
    {
      section: t('nav.section_certificates'),
      items: [
        { path: '/customers', label: t('nav.customers'), icon: 'users' },
        { path: '/certificates', label: t('nav.certificates'), icon: 'file-certificate' },
      ],
    },
    {
      section: t('nav.section_system'),
      items: [
        { path: '/admin', label: t('nav.admin'), icon: 'settings', adminOnly: true },
      ],
    },
  ];
}

// Lazy-load pages
const pages = {
  dashboard:         () => import('./pages/dashboard.js'),
  login:             () => import('./pages/login.js'),
  customers:         () => import('./pages/customers.js'),
  customerDetail:    () => import('./pages/customer-detail.js'),
  certificates:      () => import('./pages/certificates.js'),
  certificateDetail: () => import('./pages/certificate-detail.js'),
  admin:             () => import('./pages/admin.js'),
  uploadForm:        () => import('./public/upload-form.js'),
};

function renderAppShell() {
  const app = document.getElementById('app');
  app.innerHTML = '';

  const stripe = document.createElement('div');
  stripe.className = 'accent-stripe';
  app.appendChild(stripe);

  const layout = document.createElement('div');
  layout.className = 'app-layout';

  const mobileHeader = renderMobileHeader({ appTitle: t('nav.mobile_title') });
  const overlay = createSidebarOverlay();

  const sidebar = renderNavigation({
    navConfig: navConfig(),
    appTitle: t('nav.app_title'),
    fallbackInitial: 'C', // i18n-ignore: logo fallback monogram, not text
  });

  const mainWrapper = document.createElement('div');
  mainWrapper.style.cssText = 'display:flex;flex-direction:column;flex:1;min-width:0;';

  const toolbar = document.createElement('div');
  toolbar.className = 'global-toolbar';
  toolbar.style.cssText = 'display:flex;align-items:center;justify-content:flex-end;padding:0 var(--space-4);height:48px;border-bottom:1px solid var(--border-secondary);background:var(--surface-primary);gap:var(--space-2);flex-shrink:0;';

  const bellBtn = createNotificationBell();
  toolbar.appendChild(bellBtn);

  const main = document.createElement('main');
  main.className = 'main-content';
  main.id = 'main-content';

  mainWrapper.appendChild(toolbar);
  mainWrapper.appendChild(main);

  const notifSidebar = createNotificationSidebar();

  layout.appendChild(mobileHeader);
  layout.appendChild(overlay);
  layout.appendChild(sidebar);
  layout.appendChild(mainWrapper);
  app.appendChild(layout);
  app.appendChild(notifSidebar);

}

async function renderPage(loader, params = {}) {
  const main = document.getElementById('main-content');
  if (!main) return;

  main.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:50vh">
      <div class="spinner"></div>
    </div>
  `;

  try {
    const module = await loader();
    main.innerHTML = '';
    await module.render(main, params);
  } catch (err) {
    console.error('Page render error:', err);
    main.innerHTML = `
      <div class="main-content-inner">
        <div class="empty-state">
          <p style="color: var(--red-600); font-weight: 500;">${t('common.something_went_wrong')}</p>
          <p>${err.message || t('app.page_load_failed')}</p>
          <button class="btn btn-primary mt-4" onclick="location.reload()">${t('app.reload')}</button>
        </div>
      </div>
    `;
  }
}

async function init() {
  await loadBranding().catch(() => {});
  const hasSession = await auth.checkSession();
  // Public routes (no auth required)
  const publicPaths = ['/login', '/upload'];
  // The customer form always renders bare — including for a signed-in admin
  // previewing it, who would otherwise get the public form nested inside the
  // admin shell (sidebar, bell, AI widget) and not see what customers see.
  const standalonePaths = ['/upload'];
  const bootPath = window.location.pathname;
  const isCustomerForm = standalonePaths.some(
    p => bootPath === p || bootPath.startsWith(p + '/')
  );

  // Before anything renders: the user's stored locale (null when signed out).
  // ⚠️ The public upload form is CUSTOMER-facing and stays English until the
  // customer-language track (Wave B) gives a customer the language of their ERP
  // ENTITY — never their browser's, and never the previewing admin's. Its API
  // errors, the AI's rejection reason and the customer emails are all English,
  // so a translated shell around them would be a mixed-language page. Pinned
  // without persisting, so a staff member's own choice is untouched.
  const bootUser = isCustomerForm
    ? { locale: CUSTOMER_FORM_LOCALE }
    : (hasSession ? store.get('user') : null);
  await initI18n(bootUser).catch(() => {});

  router
    .on('/', (params) => renderPage(pages.dashboard, params))
    .on('/customers', (params) => renderPage(pages.customers, params))
    .on('/customers/:id', (params) => renderPage(pages.customerDetail, params))
    .on('/certificates', (params) => renderPage(pages.certificates, params))
    .on('/certificates/:id', (params) => renderPage(pages.certificateDetail, params))
    .on('/admin', (params) => renderPage(pages.admin, params))
    .on('/upload', (params) => renderPage(pages.uploadForm, params))
    .on('/upload/:token', (params) => renderPage(pages.uploadForm, params))
    .on('/login', (params) => renderPage(pages.login, params));

  router.before((path) => {
    // An AUTHENTICATED user must never be left on the login page. Waving
    // /login through unconditionally renders the login FORM inside the app
    // shell — the app looks signed in and signed out at once, and only a
    // manual reload clears it. Not an edge case: api.js bounces here on any
    // 401 while the httpOnly cookie usually survives, so the reload lands
    // authenticated; the SSO broker's ?next and plain bookmarks reach it too.
    // Fixed in isg-app-template 2026-07-18; back-propagated 2026-07-31.
    if (path === '/login' && store.get('isAuthenticated')) {
      router.navigate('/');
      return false;
    }
    if (publicPaths.some(p => path.startsWith(p))) return true;
    if (!store.get('isAuthenticated')) {
      router.navigate('/login');
      return false;
    }
    return true;
  });

  const currentPath = window.location.pathname;
  const isPublicRoute = publicPaths.some(p => currentPath.startsWith(p));
  const isStandaloneRoute = standalonePaths.some(
    p => currentPath === p || currentPath.startsWith(p + '/')
  );

  router.after(() => {
    setTimeout(() => {
      applyBranding();
    }, 50);
  });

  if (isStandaloneRoute || (!hasSession && isPublicRoute)) {
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    await router.resolve();
    applyBranding();
  } else if (!hasSession) {
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    const module = await pages.login();
    module.render(document.getElementById('main-content'));
    if (currentPath !== '/login') {
      window.history.replaceState(null, '', '/login');
    }
    applyBranding();
  } else {
    renderAppShell();
    checkEnvBanner(); // beta/test banner (no-op on live)
    await router.resolve();
    applyBranding();
    const aiMount = document.createElement('div');
    aiMount.id = 'ai-assistant-mount';
    document.body.appendChild(aiMount);
    renderAIAssistant(aiMount);
  }
}

init().catch((err) => {
  console.error('Failed to initialize app:', err);
  document.getElementById('app').innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;flex-direction:column;gap:1rem;">
      <p>${t('app.boot_failed')}</p>
      <button class="btn btn-primary" onclick="location.reload()">${t('app.reload')}</button>
    </div>
  `;
});
